/**
 * CSV Template + Field Reference generation for a configured profile. Pure functions (unit-tested)
 * + a browser download helper. Uses correct RFC-4180 quoting; contains no secrets or internal ids.
 */

/** RFC-4180: quote a field if it contains a comma, quote, CR or LF; double embedded quotes. */
export function csvEscape(value: string): string {
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

export function toCsvRow(cells: string[]): string {
  return cells.map(csvEscape).join(",");
}

/**
 * The expected source header row for a CSV profile, from its configured source columns (the mapped
 * columns). Optionally one blank data row to make the shape unambiguous. No fabricated values.
 */
export function buildCsvTemplate(
  headers: string[],
  includeBlankRow = true
): string {
  const rows = [toCsvRow(headers)];
  if (includeBlankRow) {
    rows.push(toCsvRow(headers.map(() => "")));
  }
  return rows.join("\r\n") + "\r\n";
}

export interface FieldRefRow {
  sourceColumn: string;
  iconikField: string;
  fieldType: string;
  multiValue: boolean;
  strategy: string;
  valueDelimiter: string;
  allowedValues: string[];
}

const FIELD_REF_HEADERS = [
  "Source Column",
  "iconik Field",
  "Field Type",
  "Multi-value",
  "Strategy",
  "Value Delimiter",
  "Allowed Values",
];

/** A field reference so an operator/customer can build a valid input file without guessing. */
export function buildFieldReferenceCsv(rows: FieldRefRow[]): string {
  const out = [toCsvRow(FIELD_REF_HEADERS)];
  for (const r of rows) {
    out.push(
      toCsvRow([
        r.sourceColumn,
        r.iconikField,
        r.fieldType,
        r.multiValue ? "yes" : "no",
        r.strategy,
        r.multiValue ? r.valueDelimiter : "",
        r.allowedValues.join(" | "),
      ])
    );
  }
  return out.join("\r\n") + "\r\n";
}

/** Trigger a client-side CSV download (no server round-trip). */
export function downloadCsv(filename: string, content: string): void {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
